import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Button, Input } from 'antd';
import config from '../config';
import useSandbox from '../hooks/useSandbox';
import useChatFeature, { setChatEnabled } from '../hooks/useChatFeature';
import { updateSettings } from '../services/SettingsService';
import { AI_TOOLS, ToolInstall, ToolPicker, connectionOf } from '../components/sandbox/ConnectToolModal';
import ModelKeyForm from '../components/sandbox/ModelKeyForm';
import { modelChoices } from '../components/sandbox/modelChoices';
import '../components/sandbox/sandbox.css';
import './SandboxHome.css';
import './Welcome.css';

const newConversationId = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * First-run setup (sandbox-first, docs/SANDBOX_FIRST.md): how does this person want to build? From their AI tool
 * (only the sandbox is on), with Flow's assistant (Chat, agents and playbooks too), or both. Then the setup for what
 * they picked, then a first try. The choice is saved in the person's settings and can be changed later from
 * Features › Chat and Home › Work from your AI tool.
 */
export default function Welcome({ user = null }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [useTool, setUseTool] = useState(true);
  const [useFlowPicked, setUseFlow] = useState(false);
  // Flow's assistant is offered only when the person's plan includes chat.
  const [, , chatAvailable] = useChatFeature();
  const useFlow = useFlowPicked && chatAvailable;
  const [toolKey, setToolKey] = useState(AI_TOOLS[0].key);
  const [keySourcePicked, setKeySource] = useState(config.chatOrgModels ? 'org' : 'own');
  const [ownKey, setOwnKey] = useState(undefined);
  const [prompt, setPrompt] = useState('');
  const [saving, setSaving] = useState(false);
  const tool = AI_TOOLS.find((item) => item.key === toolKey) || AI_TOOLS[0];
  // Step 2 reads what the plan includes and, with an AI tool, its live connection check; step 3 waits for its first call.
  const { sandbox, refresh } = useSandbox({ enabled: step >= 2, intervalMs: step === 3 && useTool ? 3000 : step === 2 && useTool ? 5000 : 30000 });
  const connected = connectionOf(tool, sandbox);
  const firstName = (user?.name || user?.given_name || '').split(' ')[0];
  const { org, own } = modelChoices(sandbox);
  const keySource = keySourcePicked === 'org' && !org.available && own.available ? 'own' : keySourcePicked;
  const flowReady = (keySource === 'org' && org.available) || (keySource === 'own' && own.available && Boolean(ownKey?.configured));

  const steps = useMemo(() => [
    { title: 'How to build', sub: [useTool && 'My AI tool', useFlow && 'Flow’s assistant'].filter(Boolean).join(' + ') || '—' },
    { title: 'Set it up', sub: [useTool && tool.name, useFlow && (keySource === 'org' ? 'admin’s key' : 'own key')].filter(Boolean).join(' + ') || '—' },
    { title: 'Try it', sub: 'Your first app' },
  ], [useTool, useFlow, tool.name, keySource]);

  const finish = async(next) => {
    setSaving(true);
    const assistant = useFlow && flowReady;
    setChatEnabled(assistant);
    await updateSettings({
      onboarding: { completed: true, aiTool: useTool, flowAssistant: assistant, keySource: useFlow ? keySource : null, completedAt: new Date().toISOString() },
    }).catch(() => {});
    setSaving(false);
    next(assistant);
  };

  // Home; with Flow's assistant just turned on, it starts with the prompt and the cursor is in it.
  const skip = () => finish((assistant) => navigate('/', { replace: true, state: assistant ? { focusPrompt: true } : undefined }));
  const build = () => finish(() => navigate(`/chat?conversationId=${newConversationId()}`, { state: { initialPrompt: prompt, mode: 'agent' } }));

  return (
    <div className="sbx-home welcome">
      <div className="sbx-home-inner welcome-inner">
        <div className="welcome-top">
          <strong className="welcome-brand">Flow</strong>
          <button className="sbx-link" disabled={saving} onClick={skip} type="button">Skip, go to my sandbox</button>
        </div>
        <div className="sbx-home-title">
          <h1>{firstName ? `Welcome, ${firstName}. Your sandbox is ready.` : 'Welcome. Your sandbox is ready.'}</h1>
          <p>A private machine for your projects, with live previews. Build from the AI tool you already use, or with Flow’s own assistant, or both. You can change this any time.</p>
        </div>

        <ol aria-label="Steps" className="welcome-steps">
          {steps.map((item, index) => {
            const number = index + 1;
            const state = number < step ? 'done' : number === step ? 'current' : 'next';
            return (
              <li aria-current={state === 'current' ? 'step' : undefined} className={`welcome-step ${state}`} key={item.title}>
                <span className="welcome-step-num">{state === 'done' ? '✓' : number}</span>
                <span className="welcome-step-text"><strong>{item.title}</strong><span>{item.sub}</span></span>
              </li>
            );
          })}
        </ol>

        <section className="sbx-card welcome-card">
          {step === 1 && (
            <>
              <div className="welcome-head"><h2>How do you want to build?</h2><span>Pick one or both.</span></div>
              <div className="welcome-choices">
                <button aria-pressed={useTool} className={`welcome-choice${useTool ? ' on' : ''}`} onClick={() => setUseTool(!useTool)} type="button">
                  <span className="welcome-choice-title"><strong>Connect my AI tool</strong><span aria-hidden="true" className="welcome-check">{useTool ? '✓' : ''}</span></span>
                  <span>Claude, Gemini CLI, Cursor, Kiro… work on your sandbox through MCP. Flow is where you look at code, services, logs and usage.</span>
                  <span className="welcome-tags"><span className="sbx-pill sbx-pill-running">Turns on: Sandbox</span><span className="sbx-pill">Included</span></span>
                </button>
                {chatAvailable && (
                  <button aria-pressed={useFlow} className={`welcome-choice${useFlow ? ' on' : ''}`} onClick={() => setUseFlow(!useFlow)} type="button">
                    <span className="welcome-choice-title"><strong>Use Flow’s assistant</strong><span aria-hidden="true" className="welcome-check">{useFlow ? '✓' : ''}</span></span>
                    <span>Build and fix by chatting in Flow, next to your code and previews, with the company’s AI playbooks. Uses a model key: the one your admin provides, or your own.</span>
                    <span className="welcome-tags"><span className="sbx-pill sbx-pill-running">Turns on: Sandbox, Chat, agents, playbooks</span></span>
                  </button>
                )}
              </div>
              <div className="welcome-actions">
                <Button disabled={!useTool && !useFlow} onClick={() => setStep(2)} type="primary">Continue</Button>
                <span>Change it later in Features › Chat and from Home › Work from your AI tool.</span>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              {useTool && (
                <div className="welcome-block">
                  <h2>Your AI tool</h2>
                  <ToolPicker onChange={setToolKey} sandbox={sandbox} value={tool.key} />
                  <ToolInstall email={user?.email || null} refresh={refresh} sandbox={sandbox} tool={tool} />
                </div>
              )}
              {useTool && useFlow && <hr className="welcome-sep" />}
              {useFlow && (
                <div className="welcome-block">
                  <h2>Flow’s assistant: which model key?</h2>
                  <div className="welcome-choices" role="radiogroup" aria-label="Model key">
                    <button aria-checked={keySource === 'org'} className={`welcome-choice small${keySource === 'org' ? ' on' : ''}`} disabled={!org.available} onClick={() => setKeySource('org')} role="radio" type="button">
                      <strong>The one my admin provides</strong>
                      <span>
                        {!org.inPlan ? org.note
                          : config.chatOrgModels ? 'Through AI Foundry. Nothing to paste; usage goes to your organization’s budget.' : 'Not available: your organization does not provide models.'}
                      </span>
                    </button>
                    <button aria-checked={keySource === 'own'} className={`welcome-choice small${keySource === 'own' ? ' on' : ''}`} disabled={!own.available} onClick={() => setKeySource('own')} role="radio" type="button">
                      <strong>My own API key</strong>
                      <span>{own.available ? 'From your model provider. Used only for your chats; never shown again.' : own.note}</span>
                    </button>
                  </div>
                  {keySource === 'own' && own.available && <ModelKeyForm idPrefix="welcome-key" onChange={setOwnKey} />}
                </div>
              )}
              <div className="welcome-actions">
                {/* With an AI tool, Continue stands out once the live check saw it connect. */}
                <Button onClick={() => setStep(3)} type={!useTool || connected ? 'primary' : 'default'}>Continue</Button>
                <Button onClick={() => setStep(1)}>Back</Button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2>Try it</h2>
              {useTool && (
                <div className="welcome-block">
                  <div className={`welcome-wait${connected ? ' ok' : ''}`} role="status">
                    <span aria-hidden="true" className="welcome-wait-dot" />
                    <span className="welcome-wait-text">
                      <strong>{connected ? `${tool.name} is connected` : `Waiting for ${tool.name}…`}</strong>
                      <span>{connected ? 'What it builds shows up in your sandbox.' : `This turns green as soon as ${tool.name} makes its first call to your sandbox.`}</span>
                    </span>
                  </div>
                  <span>Then ask {tool.name}:</span>
                  <code className="sbx-code">{tool.tryPrompt}</code>
                </div>
              )}
              {useFlow && flowReady && (
                <div className="welcome-block">
                  <div className="welcome-wait ok" role="status">
                    <span aria-hidden="true" className="welcome-wait-dot" />
                    <span className="welcome-wait-text"><strong>Flow’s assistant is ready</strong><span>{keySource === 'own' ? 'Using your own key (checked).' : 'Using the key your admin provides.'} Chat, agents and playbooks are on.</span></span>
                  </div>
                  <label htmlFor="welcome-prompt">Describe what to build</label>
                  <div className="welcome-prompt">
                    <Input id="welcome-prompt" onChange={(event) => setPrompt(event.target.value)} onPressEnter={() => prompt.trim() && build()} placeholder="A todo list with React and a Node backend that saves to Postgres" value={prompt} />
                    <Button disabled={!prompt.trim() || saving} onClick={build} type="primary">Build</Button>
                  </div>
                </div>
              )}
              {useFlow && !flowReady && (
                <p className="welcome-note">Flow’s assistant will be available once you save your own key (step 2) or your organization provides models. Your sandbox works now.</p>
              )}
              <div className="welcome-actions">
                <Button loading={saving} onClick={skip} type="primary">Go to my sandbox</Button>
                <Button onClick={() => setStep(2)}>Back</Button>
              </div>
            </>
          )}
        </section>
        <span className="welcome-foot">Company playbooks, MCP servers and data, production environments: optional add-ons under Features › Connections.</span>
      </div>
    </div>
  );
}

Welcome.propTypes = { user: PropTypes.object };
